export const DA_CATEGORIES = [
  { id: 'new-dwelling',     label: 'New Dwelling',     defaultOn: true  },
  { id: 'dual-occ',         label: 'Dual Occ',         defaultOn: true  },
  { id: 'multi-commercial', label: 'Multi/Commercial', defaultOn: true  },
  { id: 'alterations',      label: 'Alterations',      defaultOn: true  },
  { id: 'subdivision',      label: 'Subdivision',      defaultOn: true  },
  { id: 'rural',            label: 'Rural',            defaultOn: true  },
  { id: 'minor-works',      label: 'Minor Works',      defaultOn: false },
  { id: 'change-of-use',    label: 'Change of Use',    defaultOn: false },
  { id: 'other',            label: 'Other',            defaultOn: true  },
];

export const CATEGORY_IDS = DA_CATEGORIES.map(c => c.id);
export const LEGACY_CATEGORY_IDS = CATEGORY_IDS.filter(id => id !== 'change-of-use');
const CATEGORY_ID_SET = new Set(CATEGORY_IDS);
const CATEGORY_LABELS = new Map(DA_CATEGORIES.map(c => [c.id, c.label]));

export const DEFAULT_CATEGORIES = DA_CATEGORIES
  .filter(c => c.defaultOn)
  .map(c => c.id);

function sameSet(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  const bSet = new Set(b);
  return a.every(value => bSet.has(value));
}

export function normaliseSelectedCategories(value) {
  if (!Array.isArray(value)) {
    return { selectedCategories: DEFAULT_CATEGORIES, repaired: true };
  }

  const uniqueValid = [...new Set(value)].filter(id => CATEGORY_ID_SET.has(id));
  if (uniqueValid.length === 0) {
    return { selectedCategories: DEFAULT_CATEGORIES, repaired: true };
  }

  // Existing users who explicitly selected every old category should continue
  // to mean "all categories" after adding change-of-use.
  if (sameSet(uniqueValid, LEGACY_CATEGORY_IDS)) {
    return { selectedCategories: CATEGORY_IDS, repaired: true };
  }

  return {
    selectedCategories: uniqueValid,
    repaired: !sameSet(uniqueValid, value),
  };
}

export function getCategoryLabel(id) {
  return CATEGORY_LABELS.get(id) || 'Other';
}

export function resolveProposalType(da) {
  const aiLabel = String(da?.proposal_type_ai || '').trim();
  if (CATEGORY_ID_SET.has(aiLabel)) return aiLabel;
  return getDACategory(da);
}

export function getDACategory(da) {
  const devType = Array.isArray(da.development_type) ? da.development_type.join(', ') : (da.development_type || '');
  const desc = (da.description || da.construction_type || devType || '').toLowerCase();
  const descPlusDevType = (desc + ' ' + devType.toLowerCase()).trim();

  // ── S4.55 / consent modification / compliance certificates — always minor works ─
  if (
    desc.includes('s4.55') ||
    desc.includes('sec 4.55') ||
    desc.includes('section 4.55') ||
    desc.includes('modification of consent') ||
    desc.includes('modification to consent') ||
    desc.includes('pca') ||
    desc.includes('pca oc') ||
    desc.includes('pca cc') ||
    desc.includes('pca cdc') ||
    desc.includes('occupation certificate') ||
    desc.includes('construction certificate') ||
    (!desc && da.category && da.category.toLowerCase().includes('construction certificate')) ||
    (!desc && da.category && da.category.toLowerCase().includes('complying dev'))
  ) return 'minor-works';

  // ── Dual Occupancy — before new-dwelling ────────────────────────────────────
  if (
    descPlusDevType.includes('dual occupancy') ||
    descPlusDevType.includes('secondary dwelling') ||
    descPlusDevType.includes('granny flat') ||
    descPlusDevType.includes('duplex')
  ) return 'dual-occ';

  // ── Multi Dwelling / Commercial — check early, before alterations ────────────
  if (
    descPlusDevType.includes('multi dwelling') ||
    descPlusDevType.includes('multi-dwelling') ||
    descPlusDevType.includes('residential flat') ||
    descPlusDevType.includes('townhouse') ||
    descPlusDevType.includes('apartment') ||
    descPlusDevType.includes('mixed use') ||
    descPlusDevType.includes('commercial') ||
    descPlusDevType.includes('industrial') ||
    (descPlusDevType.includes('shop') && (descPlusDevType.includes('fitout') || descPlusDevType.includes('fit out'))) ||
    descPlusDevType.includes('shop top housing') ||
    descPlusDevType.includes('shop-top housing') ||
    descPlusDevType.includes('office') ||
    descPlusDevType.includes('warehouse') ||
    descPlusDevType.includes('hotel') ||
    descPlusDevType.includes('motel') ||
    descPlusDevType.includes('child care') ||
    descPlusDevType.includes('childcare') ||
    descPlusDevType.includes('seniors living') ||
    descPlusDevType.includes('seniors housing') ||
    descPlusDevType.includes('independent living') ||
    descPlusDevType.includes('boarding house') ||
    descPlusDevType.includes('group home') ||
    descPlusDevType.includes('tourist') ||
    descPlusDevType.includes('community facility') ||
    descPlusDevType.includes('place of public worship') ||
    descPlusDevType.includes('service station') ||
    descPlusDevType.includes('educational establishment') ||
    descPlusDevType.includes('school') ||
    descPlusDevType.includes('tafe') ||
    descPlusDevType.includes('retail premises') ||
    descPlusDevType.includes('retail premise') ||
    descPlusDevType.includes('business premises') ||
    descPlusDevType.includes('business premise') ||
    descPlusDevType.includes('general industry') ||
    descPlusDevType.includes('light industry') ||
    descPlusDevType.includes('heavy industry') ||
    descPlusDevType.includes('build-to-rent') ||
    descPlusDevType.includes('build to rent') ||
    descPlusDevType.includes('residential accommodation') ||
    descPlusDevType.includes('health services facility') ||
    descPlusDevType.includes('medical centre') ||
    descPlusDevType.includes('veterinary') ||
    descPlusDevType.includes('funeral home') ||
    descPlusDevType.includes('registered club') ||
    descPlusDevType.includes('pub ') ||
    descPlusDevType.includes('entertainment facility') ||
    descPlusDevType.includes('recreation facility') ||
    descPlusDevType.includes('information and education facility') ||
    descPlusDevType.includes('respite day centre') ||
    descPlusDevType.includes('waste management') ||
    descPlusDevType.includes('waste facility') ||
    descPlusDevType.includes('resource management facility') ||
    descPlusDevType.includes('resource recovery')
  ) return 'multi-commercial';

  // ── New Dwelling — specific unambiguous patterns ────────────────────────────
  if (
    (
      descPlusDevType.includes('dwelling house') ||
      descPlusDevType.includes('new dwelling') ||
      descPlusDevType.includes('new house') ||
      descPlusDevType.includes('erect a dwelling') ||
      descPlusDevType.includes('erect dwelling') ||
      descPlusDevType.includes('erection of dwelling') ||
      descPlusDevType.includes('erection of a new structure')
    ) &&
    !descPlusDevType.includes('alteration') &&
    !descPlusDevType.includes('addition') &&
    !descPlusDevType.includes('modification')
  ) return 'new-dwelling';

  // ── New Dwelling — bare 'dwelling' catch-all (covers portal devType combos and
  //    Tweed pattern). Guards prevent ancillary works on existing dwellings. ────
  if (
    descPlusDevType.includes('dwelling') &&
    !descPlusDevType.includes('alteration') &&
    !descPlusDevType.includes('addition') &&
    !descPlusDevType.includes('modification') &&
    !descPlusDevType.includes('raise') &&
    !descPlusDevType.includes('secondary') &&
    !descPlusDevType.includes('dual') &&
    !descPlusDevType.includes('existing dwelling') &&
    !descPlusDevType.includes('existing house') &&
    !descPlusDevType.includes('at dwelling') &&
    !descPlusDevType.includes('to dwelling') &&
    !descPlusDevType.includes('near dwelling') &&
    !descPlusDevType.includes('adjacent dwelling')
  ) return 'new-dwelling';

  // ── Subdivision ─────────────────────────────────────────────────────────────
  if (
    descPlusDevType.includes('subdivision') ||
    descPlusDevType.includes('strata title') ||
    descPlusDevType.includes('torrens title') ||
    descPlusDevType.includes('boundary adjustment') ||
    descPlusDevType.includes('lot consolidation')
  ) return 'subdivision';

  // ── Alterations & Additions ─────────────────────────────────────────────────
  if (
    descPlusDevType.includes('alteration') ||
    descPlusDevType.includes('addition') ||
    descPlusDevType.includes('renovation') ||
    descPlusDevType.includes('extension') ||
    descPlusDevType.includes('house raise') ||
    descPlusDevType.includes('raise existing') ||
    descPlusDevType.includes('raise dwelling') ||
    descPlusDevType.includes('convert') ||
    descPlusDevType.includes('internal works') ||
    descPlusDevType.includes('refurbish')
  ) return 'alterations';

  // ── Thin modification descriptions with no stronger category signal ─────────
  if (desc.includes('modification to ')) return 'minor-works';

  // ── Rural / Agricultural ─────────────────────────────────────────────────────
  if (
    descPlusDevType.includes('farm building') ||
    descPlusDevType.includes('farm shed') ||
    descPlusDevType.includes('rural shed') ||
    descPlusDevType.includes('rural worker') ||
    descPlusDevType.includes('agricultural') ||
    descPlusDevType.includes('agriculture') ||
    descPlusDevType.includes('dairy') ||
    descPlusDevType.includes('poultry') ||
    descPlusDevType.includes('livestock') ||
    descPlusDevType.includes('aquaculture') ||
    descPlusDevType.includes('horticulture') ||
    descPlusDevType.includes('intensive plant') ||
    descPlusDevType.includes('intensive animal') ||
    descPlusDevType.includes('rural industry') ||
    descPlusDevType.includes('rural land sharing') ||
    descPlusDevType.includes('extractive industry') ||
    descPlusDevType.includes('sawmill') ||
    descPlusDevType.includes('stock yard') ||
    descPlusDevType.includes('stockyard')
  ) return 'rural';

  // ── Minor Works — pools, sheds, fences etc., only when no significant
  //    dwelling/construction context implies it's part of a larger project ──────
  // Mirrors the bare-dwelling gate guards: "at/to/near dwelling" DAs are ancillary
  // works on an existing dwelling, so dwelling alone is NOT significant context for them.
  const hasSignificantContext =
    (
      descPlusDevType.includes('dwelling') &&
      !descPlusDevType.includes('at dwelling') &&
      !descPlusDevType.includes('to dwelling') &&
      !descPlusDevType.includes('near dwelling') &&
      !descPlusDevType.includes('adjacent dwelling') &&
      !descPlusDevType.includes('existing dwelling') &&
      !descPlusDevType.includes('existing house')
    ) ||
    descPlusDevType.includes('construction');

  if (!hasSignificantContext && (
    descPlusDevType.includes('swimming pool') ||
    descPlusDevType.includes(' pool') ||
    descPlusDevType.includes('spa') ||
    descPlusDevType.includes('fence') || descPlusDevType.includes('fencing') ||
    descPlusDevType.includes('carport') ||
    descPlusDevType.includes('shed') ||
    descPlusDevType.includes('retaining wall') ||
    descPlusDevType.includes('decking') ||
    (descPlusDevType.includes('deck') && !descPlusDevType.includes('residential')) ||
    descPlusDevType.includes('signage') ||
    descPlusDevType.includes('advertising sign') ||
    descPlusDevType.includes('awning') ||
    descPlusDevType.includes('driveway') ||
    descPlusDevType.includes('tree removal') || descPlusDevType.includes('tree pruning') || descPlusDevType.includes('tree works') ||
    descPlusDevType.includes('tree management') ||
    descPlusDevType.includes('vegetation management') ||
    descPlusDevType.includes('temporary building') ||
    descPlusDevType.includes('temporary structure') ||
    descPlusDevType.includes('temporary use') ||
    descPlusDevType.includes('change of use') ||
    descPlusDevType.includes('rainwater tank') ||
    descPlusDevType.includes('solar panel') ||
    descPlusDevType.includes('photovoltaic') ||
    descPlusDevType.includes('air conditioning') ||
    descPlusDevType.includes('ancillary development') ||
    descPlusDevType.includes('earthworks') ||
    descPlusDevType.includes('bush fire') ||
    descPlusDevType.includes('bushfire') ||
    (descPlusDevType.includes('demolition') && !descPlusDevType.includes('construction') && !descPlusDevType.includes('new'))
  )) return 'minor-works';

  return 'other';
}
